import { c as _c } from "react/compiler-runtime";
import { Badge } from "@/components/Badge";
import { ExperienceHeader } from "@/components/section/journey/ExperienceHeader";
import { useScrollToSection } from "@/hooks/useScrollToSection";
import { cn } from "@/utils/helpers";
import { ExperienceEntry } from "@/utils/interfaces";
import { Award, ChevronDown, ClipboardList } from "lucide-react";
import { useEffect } from "react";
interface ExperienceCardMobileProps {
  experience: ExperienceEntry;
  isExpanded: boolean;
  onToggle: () => void;
  responsibilitiesLabel: string;
  technologiesLabel: string;
}
const SCROLL_DEFAULT_DELAY = 350;
const getCardStyles = (isExpanded: boolean) => cn("glass border-none rounded-4xl transition-[opacity,box-shadow] duration-300", isExpanded ? "glow" : "opacity-85");
const getExpandableStyles = (isExpanded: boolean) => cn("grid transition-[grid-template-rows,opacity] duration-300 ease-in-out", isExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0");
const getChevronStyles = (isExpanded: boolean) => cn("w-5 h-5 shrink-0 mt-1 transition-transform duration-300 text-(--color-foreground)", isExpanded && "rotate-180");
export const ExperienceCardMobile = t0 => {
  const $ = _c(55);
  const {
    experience,
    isExpanded,
    onToggle,
    responsibilitiesLabel,
    technologiesLabel
  } = t0;
  const {
    scrollToSection
  } = useScrollToSection();
  const {
    id,
    title,
    company,
    description,
    responsibilities: t1,
    technologies
  } = experience;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const responsibilities = t2;
  let t3;
  let t4;
  if ($[2] !== id || $[3] !== isExpanded || $[4] !== scrollToSection) {
    t3 = () => {
      if (isExpanded) {
        const timeoutId = setTimeout(() => {
          scrollToSection(id);
        }, SCROLL_DEFAULT_DELAY);
        return () => clearTimeout(timeoutId);
      }
    };
    t4 = [isExpanded, id, scrollToSection];
    $[2] = id;
    $[3] = isExpanded;
    $[4] = scrollToSection;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== isExpanded) {
    t5 = getCardStyles(isExpanded);
    $[7] = isExpanded;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = `content-${id}`;
  const t7 = `${title} at ${company}. ${isExpanded ? "Collapse" : "Expand"} details.`;
  let t8;
  if ($[9] !== experience) {
    t8 = <div className="flex-1 min-w-0"><ExperienceHeader experience={experience} size="sm" /></div>;
    $[9] = experience;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== isExpanded) {
    t9 = getChevronStyles(isExpanded);
    $[11] = isExpanded;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t9) {
    t10 = <ChevronDown className={t9} aria-hidden="true" />;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t10 || $[16] !== t8) {
    t11 = <div className="flex items-start justify-between gap-2">{t8}{t10}</div>;
    $[15] = t10;
    $[16] = t8;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== isExpanded || $[19] !== onToggle || $[20] !== t11 || $[21] !== t6 || $[22] !== t7) {
    t12 = <button type="button" onClick={onToggle} className="w-full p-4 text-left cursor-pointer" aria-expanded={isExpanded} aria-controls={t6} aria-label={t7}>{t11}</button>;
    $[18] = isExpanded;
    $[19] = onToggle;
    $[20] = t11;
    $[21] = t6;
    $[22] = t7;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = `content-${id}`;
  let t14;
  if ($[24] !== isExpanded) {
    t14 = getExpandableStyles(isExpanded);
    $[24] = isExpanded;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  const t15 = !isExpanded;
  let t16;
  if ($[26] !== description) {
    t16 = <p className="body-base mb-6 leading-relaxed">{description}</p>;
    $[26] = description;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== responsibilities || $[29] !== responsibilitiesLabel) {
    t17 = responsibilities.length > 0 && <div className="mb-6"><div className="flex items-center gap-2 mb-4"><ClipboardList className="w-5 h-5 text-(--color-accent-3)" aria-hidden="true" /><h3 className="heading-4">{responsibilitiesLabel}</h3></div><ul className="flex flex-col gap-2">{responsibilities.map(_temp)}</ul></div>;
    $[28] = responsibilities;
    $[29] = responsibilitiesLabel;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Award className="w-5 h-5 text-(--color-accent-3)" aria-hidden="true" />;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== technologiesLabel) {
    t19 = <div className="flex items-center gap-2 mb-4">{t18}<h3 className="heading-4">{technologiesLabel}</h3></div>;
    $[32] = technologiesLabel;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== technologies) {
    t20 = technologies.map(_temp2);
    $[34] = technologies;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== t20) {
    t21 = <ul className="flex flex-wrap gap-2">{t20}</ul>;
    $[36] = t20;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== t19 || $[39] !== t21) {
    t22 = <div>{t19}{t21}</div>;
    $[38] = t19;
    $[39] = t21;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== t16 || $[42] !== t17 || $[43] !== t22) {
    t23 = <div className="overflow-hidden"><div className="px-4 pb-4">{t16}{t17}{t22}</div></div>;
    $[41] = t16;
    $[42] = t17;
    $[43] = t22;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== t13 || $[46] !== t14 || $[47] !== t15 || $[48] !== t23) {
    t24 = <div id={t13} className={t14} aria-hidden={t15}>{t23}</div>;
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
    $[48] = t23;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== id || $[51] !== t12 || $[52] !== t24 || $[53] !== t5) {
    t25 = <div id={id} role="listitem" className={t5}>{t12}{t24}</div>;
    $[50] = id;
    $[51] = t12;
    $[52] = t24;
    $[53] = t5;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  return t25;
};
function _temp(responsibility, index) {
  return <li key={index} className="body-base flex gap-2"><span aria-hidden="true">•</span><span>{responsibility}</span></li>;
}
function _temp2(tech) {
  return <li key={tech}><Badge>{tech}</Badge></li>;
}